/* Delcube Header: bekommt ab 8 px Scrolltiefe einen blickdichten Grund und eine feine Linie. */
(() => {
  const root = document.documentElement;
  let ticking = false;

  const update = () => {
    root.classList.toggle('dc-scrolled', window.scrollY > 8);
    ticking = false;
  };

  window.addEventListener('scroll', () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(update);
    }
  }, { passive: true });

  update();
})();

/* Delcube Warenkorb-Stepper: Plus/Minus ändern das Mengenfeld sofort. Nach 300 ms Ruhe
   wird ein "change"-Event ausgelöst, das die Theme-Logik (line-item-quantity) übernimmt. */
(() => {
  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-dc-step]') : null;
    if (!button) return;

    const input = button.parentElement?.querySelector('input[type="number"]');
    if (!input || input.disabled) return;

    const step = Number(input.step) || 1;
    const min = input.min !== '' ? Number(input.min) : 1;
    const max = input.max !== '' ? Number(input.max) : Infinity;
    const current = Number(input.value) || min;
    const next = Math.min(max, Math.max(min, current + Number(button.dataset.dcStep) * step));

    if (next === current) return;

    input.value = String(next);
    input.style.setProperty('--quantity-input-characters-count', `${String(next).length}ch`);

    // Während die Änderung aussteht, ist die Position als "beschäftigt" markiert (Screenreader, doppelte Klicks)
    const lineItem = input.closest('line-item');
    lineItem?.setAttribute('aria-busy', 'true');

    clearTimeout(input.dcTimer);
    input.dcTimer = setTimeout(() => {
      input.dispatchEvent(new Event('change', { bubbles: true }));
      document.documentElement.addEventListener('cart:change', () => lineItem?.removeAttribute('aria-busy'), { once: true });
      setTimeout(() => lineItem?.removeAttribute('aria-busy'), 4000);
    }, 300);
  });
})();

/* Delcube Kollektionskarten mit bewegtem Motiv (Video oder GIF).
   Maus: spielt beim Hover und bei Tastaturfokus. Touch: spielt, solange die Karte zu 60 % sichtbar ist.
   Das Medium wird erst beim ersten Abspielen geladen. GIFs starten bei jedem Hover von vorn.
   Bei "Bewegung reduzieren" bleibt das statische Bild stehen. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const FADE_MS = 250;

  const mediaOf = (card) => card.querySelector('.dc-hover-media__item');

  const play = (card) => {
    if (reduceMotion.matches) return;
    const media = mediaOf(card);
    if (!media) return;

    clearTimeout(card.dcStopTimer);
    card.classList.add('is-playing');

    if (media.tagName === 'VIDEO') {
      if (media.readyState === 0) media.load();
      media.currentTime = 0;
      media.play().catch(() => {});
    } else if (!media.getAttribute('src')) {
      media.src = media.dataset.src;
    }
  };

  const stop = (card) => {
    const media = mediaOf(card);
    card.classList.remove('is-playing');
    if (!media) return;

    // Erst nach dem Ausblenden anhalten bzw. das GIF entladen, damit es beim nächsten Mal von vorn startet
    card.dcStopTimer = setTimeout(() => {
      if (media.tagName === 'VIDEO') {
        media.pause();
      } else {
        media.removeAttribute('src');
        media.classList.remove('is-ready');
      }
    }, FADE_MS);
  };

  const markReady = (event) => {
    if (event.target.classList?.contains('dc-hover-media__item')) event.target.classList.add('is-ready');
  };

  document.addEventListener('load', markReady, true);
  document.addEventListener('playing', markReady, true);

  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => (entry.isIntersecting ? play(entry.target) : stop(entry.target)));
      }, { threshold: 0.6 })
    : null;

  const bind = (scope = document) => {
    scope.querySelectorAll('[data-dc-hover-media]:not([data-dc-bound])').forEach((card) => {
      card.setAttribute('data-dc-bound', '');
      card.addEventListener('mouseenter', () => finePointer.matches && play(card));
      card.addEventListener('mouseleave', () => finePointer.matches && stop(card));
      card.addEventListener('focusin', () => play(card));
      card.addEventListener('focusout', () => stop(card));
      if (!finePointer.matches && observer) observer.observe(card);
    });
  };

  bind();
  document.addEventListener('shopify:section:load', (event) => bind(event.target));
})();

/* Delcube Backstory:
   - Videos spielen stumm, nur solange sie sichtbar sind (spart Akku und Datenvolumen).
   - Die Kapitel-Navigation markiert das Kapitel in der Bildschirmmitte, die Linie füllt sich mit dem Lesefortschritt. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const videoObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach(({ target: video, isIntersecting }) => {
          if (isIntersecting && !reduceMotion.matches) {
            video.muted = true;
            if (video.preload === 'none') video.preload = 'auto';
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      }, { threshold: 0.35 })
    : null;

  const bindStory = (story) => {
    if (story.hasAttribute('data-dc-bound')) return;
    story.setAttribute('data-dc-bound', '');

    story.querySelectorAll('.dc-story__video').forEach((video) => videoObserver?.observe(video));

    const chapters = [...story.querySelectorAll('[data-dc-story-chapter]')];
    const links = new Map([...story.querySelectorAll('[data-dc-story-link]')].map((link) => [link.dataset.dcStoryLink, link]));
    const list = story.querySelector('.dc-story__chapters');
    const fill = story.querySelector('.dc-story__progress-fill');

    if (links.size && 'IntersectionObserver' in window) {
      // Aktiv ist das Kapitel, das die Bildschirmmitte schneidet
      const chapterObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          links.forEach((link, id) => {
            if (id === entry.target.id) link.setAttribute('aria-current', 'step');
            else link.removeAttribute('aria-current');
          });
        });
      }, { rootMargin: '-45% 0px -50% 0px' });

      chapters.forEach((chapter) => chapterObserver.observe(chapter));
    }

    if (fill && list) {
      let ticking = false;
      const update = () => {
        const rect = list.getBoundingClientRect();
        const progress = Math.min(1, Math.max(0, (window.innerHeight * 0.5 - rect.top) / rect.height));
        fill.style.transform = `scaleY(${progress.toFixed(4)})`;
        ticking = false;
      };
      window.addEventListener('scroll', () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      }, { passive: true });
      update();
    }
  };

  const init = (scope = document) => scope.querySelectorAll('[data-dc-story]').forEach(bindStory);

  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
